/**
 * Environment represents the deployment environment of the application.
 */
export type Environment = 'production' | 'staging' | 'dev';

/**
 * Service represents the different frontend applications.
 */
export type Service = 'admin' | 'app' | 'clients' | 'smartfill';

/**
 * Backend represents the different backend services.
 */
export type Backend = 'api' | 'statics';

/**
 * Gets the current environment the application is running in.
 * Defaults to 'dev' if running server-side or if environment is not set.
 */
export const getEnvironment = (): Environment => {
  if (typeof window === 'undefined') {
    return 'dev';
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const environment = process.env.OYSTER_ENVIRONMENT || window?.oyster?.opts?.environment;
  switch (environment) {
    case 'production':
    case 'staging':
    case 'dev':
      return environment;
    default:
      return 'dev';
  }
};

/**
 * Gets the base URL for the API service based on the current environment.
 */
export const getApiBaseUrl = (): string => {
  // The domain used to the reach the API service for the admin app is `oysterinc.net`,
  // even though the backing service is the same. This is because admin.oysterinc.net
  // won't send the internal auth cookie to a different domain (cookie security policy).
  const domain = getServiceName() === 'admin' ? 'oysterinc.net' : 'getcara.ai';
  switch (getEnvironment()) {
    case 'production':
      return `https://api.${domain}`;
    case 'staging':
      return `https://api.staging.${domain}`;
    case 'dev':
      return devServiceUrl('api');
  }
};

/**
 * Gets the base URL for the AIS service based on the current environment.
 */
export const getAisBaseUrl = (): string => {
  switch (getEnvironment()) {
    case 'production':
      return 'https://app.getcara.ai';
    case 'staging':
      return 'https://app.staging.getcara.ai';
    case 'dev':
      return devServiceUrl('app');
  }
};

/**
 * Gets the base URL for the Smart Fill service based on the current environment.
 */
export const getSmartFillBaseUrl = (): string => {
  switch (getEnvironment()) {
    case 'production':
      return 'https://smartfill.getcara.ai';
    case 'staging':
      return 'https://smartfill.staging.getcara.ai';
    case 'dev':
      return devServiceUrl('smartfill');
  }
};

/**
 * Gets the base URL for the statics service based on the current environment.
 */
export const getStaticsBaseUrl = (): string => {
  switch (getEnvironment()) {
    case 'production':
      return 'https://s.getcara.ai';
    case 'staging':
      return 'https://s.staging.getcara.ai';
    case 'dev':
      return devServiceUrl('statics');
  }
};

/**
 * Gets the Temporal namespace for the current environment. There is a single
 * Temporal cluster (temporal.oysterinc.net) and namespaces partition it by env.
 * Dev pods use `dev-{user}` namespaces that the browser can't derive — callers
 * land on "default" and switch namespaces manually in the Temporal UI.
 */
export const getTemporalNamespace = (): string => {
  switch (getEnvironment()) {
    case 'production':
      return 'production';
    case 'staging':
      return 'staging';
    case 'dev':
      return 'default';
  }
};

/**
 * Constructs a development service URL based on the current user's subdomain.
 * Format: https://{service}.{user}.dev.oysterinc.net or https://{service}.{worktree}.{user}.dev.oysterinc.net
 */
const devServiceUrl = (service: Service | Backend) => {
  // Split the host into parts and replace the first part with the desired service name
  const parts = window.location.host.split('.');
  parts[0] = service;

  // Join the parts back together and return the URL
  return `https://${parts.join('.')}`;
};

/**
 * Gets the current service name from environment variables.
 */
export const getServiceName = (): Service => (process.env.SERVICE_NAME as Service) || '';

/**
 * Gets the current service version from environment variables.
 */
export const getServiceVersion = (): string => process.env.SERVICE_VERSION || '';

/**
 * Gets a secret value from either development environment variables or window.oyster.opts.
 */
const getSecret = (secretName: string, devSecret?: string) =>
  devSecret || window?.['oyster']?.['opts']?.[secretName];

/**
 * Gets the Mapbox public API key.
 */
export const getMapboxPublicKey = () => getSecret('mapboxPublicKey', process.env.MAPBOX_PUBLIC_KEY);

/**
 * Gets the Sentry DSN for error reporting.
 */
export const getSentryDSN = () => process.env.SENTRY_DSN;
